/**
 * Phosphor / Daylight mode switch.
 *
 * The mode itself is applied by snippets/di-theme-boot.liquid before first
 * paint; this element only flips the attribute and keeps its own labels and
 * the persisted preference in sync. Everything visual is driven by CSS from
 * `data-di-theme` on <html>, so nothing here has to know about the palette.
 */

const STORAGE_KEY = 'di-theme';

const LABELS = {
  dark: {
    action: 'Switch to Daylight, the light color mode',
    announcement: 'Phosphor dark mode on',
  },
  light: {
    action: 'Switch to Phosphor, the dark color mode',
    announcement: 'Daylight light mode on',
  },
};

function currentMode() {
  return document.documentElement.getAttribute('data-di-theme') === 'light' ? 'light' : 'dark';
}

function applyMode(mode) {
  const root = document.documentElement;
  root.setAttribute('data-di-theme', mode);
  root.style.colorScheme = mode;
}

function persistMode(mode) {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch (e) {
    // Storage unavailable (private mode, blocked cookies). The switch still
    // works for this page view, it just will not survive a reload.
  }
}

class DiThemeToggle extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-di-theme-button]');
    this.status = this.querySelector('[data-di-theme-status]');
    if (!this.button) return;

    this.onClick = this.onClick.bind(this);
    this.onStorage = this.onStorage.bind(this);
    this.syncLabel = this.syncLabel.bind(this);

    this.button.addEventListener('click', this.onClick);
    window.addEventListener('storage', this.onStorage);

    // The header renders one copy per breakpoint, so an instance cannot assume
    // it is the one that was clicked. Watching the attribute keeps every copy's
    // label right no matter which one, or which tab, changed the mode.
    this.observer = new MutationObserver(this.syncLabel);
    this.observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-di-theme'],
    });

    this.syncLabel();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
    window.removeEventListener('storage', this.onStorage);
    this.observer?.disconnect();
  }

  onClick() {
    const next = currentMode() === 'light' ? 'dark' : 'light';

    persistMode(next);
    this.switchTo(next);
  }

  /** Cross-tab sync: another tab wrote a different preference. */
  onStorage(event) {
    if (event.key !== STORAGE_KEY) return;
    const incoming = event.newValue === 'light' ? 'light' : 'dark';
    if (incoming === currentMode()) return;

    this.switchTo(incoming, { announce: false });
  }

  switchTo(mode, { announce = true } = {}) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const commit = () => {
      applyMode(mode);
      this.syncLabel();
      if (announce) this.announce(mode);
    };

    if (!reducedMotion && typeof document.startViewTransition === 'function') {
      document.startViewTransition(commit);
    } else {
      commit();
    }
  }

  syncLabel() {
    // Keyed on the current mode; each label states where pressing it goes.
    const target = currentMode() === 'light' ? 'light' : 'dark';
    this.button.setAttribute('aria-label', LABELS[target].action);
  }

  announce(mode) {
    if (!this.status) return;
    this.status.textContent = LABELS[mode].announcement;
  }
}

if (!customElements.get('di-theme-toggle')) {
  customElements.define('di-theme-toggle', DiThemeToggle);
}
